import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../../utils/axiosInterceptor';

import {
    Paper, Fab, Typography, Tooltip, Button, Container, CircularProgress, Box
} from '@mui/material';

import {
    DataGrid,
    gridPageCountSelector,
    GridPagination,
    useGridApiContext,
    GridToolbar,
    useGridSelector,
} from '@mui/x-data-grid';
import MuiPagination from '@mui/material/Pagination';

// Custom components and functions
import CustomMessage from '../../utils/components/CustomMessage';
import CustomStatusChip from '../../utils/components/CustomStatusChip';
import useWindowDimensions from '../../utils/functions/useWindowDimensions';
import { formatMoney, getDate } from '../../utils/functions/Utils';
import LocaleTextDataGrid from '../../utils/functions/LocaleTextDataGrid';

const TablaFacturas = () => {

    let navigate = useNavigate();
    const auth = useSelector((state) => state.auth);

    const [data, setData] = useState([]);
    const [selected, setSelected] = useState('');
    const [loading, setLoading] = useState(false);

    const [apiRes, setApiRes] = useState({ show: false, message: '', severity: '' });
    const { height } = useWindowDimensions();

    useEffect(() => {
        if (!['Admin'].includes(auth.user.rol)) {
            navigate('/dashboard');
        }
        document.title = 'Facturas';
    }, [auth])

    const handleCloseMessage = (show) => {
        setApiRes(state => { return { ...state, show: show } })
    }

    const getData = async () => {
        setLoading(true);
        try {
            let res = await axios.get('/api/facturas');
            if (res.status === 200) {
                if (res.data.length <= 1) {
                    res.data[0].fechaEmision = getDate(res.data[0].fechaEmision, 'D');
                    res.data[0].fechaPago = getDate(res.data[0].fechaPago, 'D');
                    res.data[0].subTotal = `$ ${formatMoney(res.data[0].subTotal)}`
                    res.data[0].iva = `$ ${formatMoney(res.data[0].iva)}`
                    res.data[0].total = `$ ${formatMoney(res.data[0].total)}`
                    res.data[0].cliente = res.data[0].receptor.denominacion;
                    if (Object.keys(res.data[0].cotizaciones).length > 0) {
                        res.data[0].noReporte = Object.keys(res.data[0].cotizaciones).map((key, i) => res.data[0].cotizaciones[i]).join(', ');
                    }
                    setData(res.data);
                }
                else {
                    let tmp = res.data.map((item) => {
                        item.fechaEmision = getDate(item.fechaEmision, 'D');
                        item.fechaPago = getDate(item.fechaPago, 'D ');
                        item.subTotal = `$ ${formatMoney(item.subTotal)}`
                        item.iva = `$ ${formatMoney(item.iva)}`
                        item.total = `$ ${formatMoney(item.total)}`
                        item.cliente = item.receptor.denominacion;
                        if (Object.keys(item.cotizaciones).length > 0) {
                            item.noReporte = Object.keys(item.cotizaciones).map((key, i) => item.cotizaciones[i]).join(', ');
                        }
                        return item;
                    });
                    setData(tmp);
                }
                document.title = 'Facturas';
                setLoading(false);
            }
        } catch (err) {
            // setApiRes(state => { return { ...state, show: true, message: 'Ocurrió un error al cargar las cotizaciones, favor de intentarlo mas tarde', severity: 'error' } });
            console.log(err);
            setLoading(true);
        }
    }

    useEffect(() => {
        getData();
    }, [])

    const renderProducts = () => {
        if (data.length < 0) return (<div>No hay facturas registradas</div>);
        if (loading) return (<CircularProgress size='5em' color='primary' />);
        return (
            <div >
                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'start', alignItems: 'center' }}>
                    <Button size='small' variant='outlined' onClick={() => navigate('/dashboard')}><i className='fad fa-arrow-left'></i> &nbsp;&nbsp;Regresar</Button>
                    <Typography variant='h5' align='left' sx={{ mt: 1, mb: 1, fontWeight: 700, ml: 3 }}>Facturas</Typography>
                </div>
                <DataGrid
                    autoPageSize={true}
                    sx={{ '& .MuiDataGrid-toolbarContainer': { position: 'fixed', top: '70px', right: '24px' }, '& .MuiButtonBase-root': { color: 'comp.gray' }, '& .MuiDataGrid-row': { backgroundColor: 'comp.tableBg', color: 'text.primary', height: 'auto' }, '& .MuiDataGrid-columnHeader': { backgroundColor: 'comp.tableHeaderBg', color: 'text.primary' } }}
                    style={{ height: `${height - 120}px`, width: '100%', overflow: 'hidden' }}
                    localeText={LocaleTextDataGrid}
                    rows={data}
                    density='compact'
                    getRowId={(row) => row._id}
                    onCellClick={(e) => { setSelected(e.row._id) }}
                    onCellDoubleClick={(e) => { navigate(`/facturas/editarFactura/${e.row._id}`, { target: '_blank' }) }}
                    columns={[
                        { field: 'noFactura', headerName: '# Factura', align: 'center', headerAlign: 'center', flex: 1, maxWidth: 120 },
                        { field: 'noReporte', headerName: '# Reporte', align: 'center', headerAlign: 'center', flex: 1, maxWidth: 120 },
                        { field: 'metodoPago', headerName: 'Método de Pago', align: 'center', headerAlign: 'center', flex: 1, maxWidth: 120 },
                        {
                            field: 'estatus',
                            flex: 1,
                            maxWidth: 200,
                            headerName: 'Estatus',
                            headerClassName: 'text-bold',
                            align: 'center',
                            headerAlign: 'center',
                            renderCell: (params) => (
                                <CustomStatusChip estatus={params.row.estatus} label={params.row.estatus} />
                            ),
                        },
                        { field: 'cliente', flex: 1, headerName: 'Cliente', align: 'center', headerAlign: 'center', minWidth: 200 },
                        { field: 'fechaEmision', flex: 1, headerName: 'Fecha de Emision', align: 'center', headerAlign: 'center', maxWidth: 200 },
                        { field: 'fechaPago', flex: 1, headerName: 'Fecha de Pago', align: 'center', headerAlign: 'center', maxWidth: 200 },
                        { field: 'subTotal', flex: 1, headerName: 'Sub total', align: 'center', headerAlign: 'center', maxWidth: 180 },
                        { field: 'iva', flex: 1, headerName: 'IVA', align: 'center', headerAlign: 'center', maxWidth: 160 },
                        { field: 'total', flex: 1, headerName: 'Total', align: 'center', headerAlign: 'center', maxWidth: 180, cellClassName: 'text-bold' },
                    ]}
                    pagination
                    slots={{
                        pagination: CustomPagination,
                        toolbar: GridToolbar
                    }}
                    initialState={{
                        ...data.initialState,
                        pagination: { paginationModel: { pageSize: 25 } },
                    }}
                />
            </div>
        )
    }

    return (
        <Container maxWidth='xxl' sx={{ width: '100%', overflow: 'hidden', margin: '0px', padding: '4px 4px !important' }}>
            <Paper>
                <Link to='/facturas/nuevaFactura'>
                    <Tooltip title='Nueva Factura' placement='top' arrow>
                        <Fab aria-label="add" color='success' style={{ position: 'fixed', bottom: '8px', left: '8px', zIndex: '10', color: '#FFF' }}>
                            <i className="fad fa-plus fa-2x"></i>
                        </Fab>
                    </Tooltip>
                </Link>

                {selected !== '' ?
                    <div>

                        <Link target='_blank' to={{ pathname: `/facturas/editarFactura/${selected}`, state: { id: selected } }} style={{ textDecoration: 'none' }}>
                            <Tooltip title='Editar' placement='top' arrow>
                                <Fab aria-label="add" color='warning' style={{ position: 'fixed', bottom: '8px', left: '98px', zIndex: '10', color: '#FFF' }}>
                                    <i className="fad fa-edit fa-2x"></i>
                                </Fab>
                            </Tooltip>
                        </Link>
                    </div>
                    : null}
                {loading ?
                    <Box style={{ display: 'flex', justifyContent: 'center', alignContent: 'center', marginTop: '128px' }}>
                        <CircularProgress size='5em' color='primary' />
                    </Box> : renderProducts()}
                <CustomMessage open={apiRes.show} message={apiRes.message} severity={apiRes.severity} handleCloseMessage={handleCloseMessage} />
            </Paper>
        </Container>
    );
}

function Pagination({ page, onPageChange, className }) {
    const apiRef = useGridApiContext();
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
        <MuiPagination
            color="primary"
            className={className}
            count={pageCount}
            page={page + 1}
            onChange={(event, newPage) => {
                onPageChange(event, newPage - 1);
            }}
        />
    );
}

function CustomPagination(props) {
    return <GridPagination ActionsComponent={Pagination} {...props} />;
}

export default TablaFacturas;